import clsx from "clsx";
import { Menu as MenuIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useShell } from "../../app/shell-context";
import { SearchTrigger } from "../../features/search/SearchTrigger";

/** Opens the rail and the column as a drawer. Shown below 768 pixels only (redesign spec §4.6). */
export function MenuButton() {
  const { openMenu } = useShell();
  return (
    <button
      type="button"
      aria-label="Menu"
      onClick={openMenu}
      className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-ink-2 hover:bg-green-wash hover:text-green-ink md:hidden"
    >
      <MenuIcon size={18} aria-hidden="true" />
    </button>
  );
}

/** One step of the breadcrumb. The last one is the page itself: `<Crumb current>`. */
export function Crumb({ children, current = false }: { children: ReactNode; current?: boolean }) {
  return (
    <li
      aria-current={current ? "page" : undefined}
      className={clsx(
        "flex min-w-0 items-center gap-2 [&:not(:first-child)]:before:text-line-strong [&:not(:first-child)]:before:content-['/']",
        current ? "text-ink" : "shrink-0 [&_a:hover]:text-ink",
      )}
    >
      <span className="truncate">{children}</span>
    </li>
  );
}

/**
 * The strip above every page except the document page (redesign spec §4.4): the breadcrumb at the left and search
 * at the right. It is for finding your way and holds no action: what a page lets you do sits beside its title
 * (page frame spec §4.1).
 */
export function PageBar({ children }: { children: ReactNode }) {
  return (
    <div className="sticky top-0 z-20 flex min-h-15 flex-wrap items-center gap-x-4 gap-y-2 border-b border-line bg-column px-4 py-3 md:px-8">
      <MenuButton />
      <nav aria-label="Breadcrumb" className="min-w-0 flex-1">
        <ol className="flex min-w-0 items-center gap-2 text-sm text-ink-3">{children}</ol>
      </nav>
      <SearchTrigger variant="field" />
    </div>
  );
}

/** A page's content area: 32px at the sides, 28px at the top (redesign spec §3.3). */
export function Page({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={clsx("px-4 pt-7 pb-10 md:px-8", className)}>{children}</div>;
}
